import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Grid, Button,
  Divider, Skeleton, Chip,
} from '@mui/material';
import { ArrowBack, OpenInNew } from '@mui/icons-material';
import { format } from 'date-fns';
import { eventsAPI } from '../services/api';
import SeverityChip from '../components/common/SeverityChip';
import EventTypeChip from '../components/common/EventTypeChip';

function DetailRow({ label, value, highlight }) {
  if (!value && value !== 0) return null;
  return (
    <Box sx={{ display: 'flex', py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.08)', alignItems: 'flex-start', gap: 2 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 180, fontWeight: 600, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: highlight || 'text.primary', fontWeight: highlight ? 700 : 400, wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    eventsAPI.getEvent(id)
      .then(res => setEvent(res.data.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2, color: 'text.secondary' }}>Back</Button>
      <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2, bgcolor: 'rgba(255,255,255,0.05)' }} />
    </Box>
  );

  if (!event) return (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <Typography variant="h6" sx={{ color: 'text.secondary' }}>Event not found</Typography>
      <Button onClick={() => navigate('/events')} sx={{ mt: 2 }}>Back to Events</Button>
    </Box>
  );

  const safeFormat = (dateStr, fmt) => {
    try {
      return format(new Date(dateStr), fmt);
    } catch {
      return dateStr;
    }
  };

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2, color: 'text.secondary', textTransform: 'none' }}>
        Back to Events
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
            <EventTypeChip type={event.type} size="medium" />
            <SeverityChip severity={event.severity} size="medium" />
            {event.flareClass && (
              <Chip label={event.flareClass} size="small" sx={{ bgcolor: 'rgba(255,167,38,0.15)', color: '#ffa726', border: '1px solid rgba(255,167,38,0.4)', fontWeight: 800, fontSize: 14 }} />
            )}
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>
            {event.type?.replace(/_/g, ' ')} Event
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {safeFormat(event.eventDate, "EEEE, MMMM dd, yyyy '—' HH:mm 'UTC'")}
          </Typography>
        </Box>
        <Button variant="outlined" size="small" endIcon={<OpenInNew />}
          href="https://kauai.ccmc.gsfc.nasa.gov/DONKI/" target="_blank" rel="noopener noreferrer"
          sx={{ borderColor: 'rgba(255,255,255,0.4)', color: 'primary.light' }}>
          View on NASA DONKI
        </Button>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Event Details</Typography>
              <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 1 }} />

              <DetailRow label="NASA ID"          value={event.nasaId} />
              <DetailRow label="Event Type"       value={event.type?.replace(/_/g, ' ')} />
              <DetailRow label="Event Date (UTC)" value={safeFormat(event.eventDate, 'yyyy-MM-dd HH:mm:ss')} />
              <DetailRow label="Severity"         value={event.severity} highlight={
                event.severity === 'EXTREME' ? '#ef5350' : event.severity === 'HIGH' ? '#ffa726' : null
              } />

              {event.flareClass && <>
                <DetailRow label="Flare Class"    value={event.flareClass} highlight="#ffa726" />
                {event.peakTime && (
                  <DetailRow label="Peak Time (UTC)" value={safeFormat(event.peakTime, 'yyyy-MM-dd HH:mm')} />
                )}
                {event.sourceLocation && (
                  <DetailRow label="Source Location" value={event.sourceLocation} />
                )}
              </>}

              {event.kpIndex != null && (
                <DetailRow label="Max Kp Index" value={`${event.kpIndex?.toFixed(1)} (${kpToScale(event.kpIndex)})`} highlight="#7e57c2" />
              )}

              {event.speedKmS    != null && <DetailRow label="Speed"         value={`${Math.round(event.speedKmS)} km/s`} highlight="#26c6da" />}
              {event.halfAngle   != null && <DetailRow label="Half Angle"    value={`${event.halfAngle?.toFixed(1)}°`} />}
              {event.angularWidth!= null && <DetailRow label="Angular Width" value={`${event.angularWidth?.toFixed(1)}°`} />}
              {event.instruments  && <DetailRow label="Instruments"   value={event.instruments} />}
              {event.linkedEvents && <DetailRow label="Linked Events"  value={event.linkedEvents} />}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          {event.description && (
            <Card sx={{ mb: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Description</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  {event.description}
                </Typography>
              </CardContent>
            </Card>
          )}
          {event.note && (
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>Notes</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  {event.note}
                </Typography>
              </CardContent>
            </Card>
          )}

          {event.kpIndex != null && (
            <Card sx={{ mt: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>G-Scale Reference</Typography>
                {[
                  { g: 'G1', kp: '5', label: 'Minor',   color: '#66bb6a' },
                  { g: 'G2', kp: '6', label: 'Moderate', color: '#ffa726' },
                  { g: 'G3', kp: '7', label: 'Strong',  color: '#ef5350' },
                  { g: 'G4', kp: '8', label: 'Severe',  color: '#b71c1c' },
                  { g: 'G5', kp: '9', label: 'Extreme', color: '#7b1fa2' },
                ].map(({ g, kp, label, color }) => (
                  <Box key={g} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5,
                    opacity: Math.floor(event.kpIndex) >= parseInt(kp) ? 1 : 0.35 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#fff', fontWeight: 800, fontSize: 10 }}>{g}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Kp ≥ {kp} — {label}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

function kpToScale(kp) {
  if (kp >= 9) return 'G5 Extreme';
  if (kp >= 8) return 'G4 Severe';
  if (kp >= 7) return 'G3 Strong';
  if (kp >= 6) return 'G2 Moderate';
  if (kp >= 5) return 'G1 Minor';
  return 'Below G1';
}